import { useRef, useEffect } from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';

import THEME from '../../constants/theme';
import heroVideo from '../../assets/default-background.mp4';

/**
 * Modern HeroBanner component for the homepage
 * Uses the brand video background with updated content for OctopusSync
 */
const HeroBanner = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Video autoplay failed - expected in many browsers
        });
      }
    }
  }, []);

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: THEME.COLORS.NEUTRAL_WHITE,
        overflow: 'hidden'
      }}
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: '50%',
          top: '50%',
          objectFit: 'cover',
          transform: 'translate(-50%, -50%)',
          zIndex: 0
        }}
      >
        <source src={heroVideo} type='video/mp4' />
        Your browser does not support the video tag.
      </video>

      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(101, 17, 175, 0.2) 100%)',
          zIndex: 1
        }}
      />

      {/* Content Container */}
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'left',
          px: { xs: 3, sm: 4, md: 6 },
          maxWidth: { xs: '100%', sm: '90%', md: '85%', lg: '80%' }
        }}
      >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={textVariants}>
            <Typography
              variant='h2'
              sx={{
                fontSize: {
                  xs: '2rem',
                  sm: '2.8rem',
                  md: '3.5rem',
                  lg: '4rem'
                },
                fontWeight: 400,
                marginBottom: 3,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                lineHeight: { xs: 1.2, md: 1.1 },
                letterSpacing: '-0.02em'
              }}
            >
              Synchronize your{' '}
              <Box 
                component='span' 
                sx={{ 
                  fontWeight: 500,
                  color: '#ffffff',
                  textShadow: '0 0 30px rgba(101, 17, 175, 0.6)'
                }}
              >
                trading power
              </Box>
            </Typography>
          </motion.div>

          <motion.div variants={textVariants}>
            <Typography
              variant='h5'
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem', lg: '1.6rem' },
                fontWeight: 400,
                marginBottom: 2,
                textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                lineHeight: 1.5,
                maxWidth: '900px',
                opacity: 0.95
              }}
            >
              OctopusSync delivers seamless integration solutions
              that connect, automate, and optimize your business processes.
            </Typography>
          </motion.div>

          <motion.div variants={textVariants}>
            <Typography
              variant='h5'
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem', lg: '1.6rem' },
                fontWeight: 700,
                marginBottom: 4,
                textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                lineHeight: 1.4,
                maxWidth: '850px',
                color: '#ffffff'
              }}
            >
              Replicate strategies. Connect accounts. Maximize performance.
            </Typography>
          </motion.div>

          <motion.div variants={textVariants}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                mt: 4
              }}
            >
              {/* CTA Buttons can be added here later */}
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroBanner;
